import { useEntitlement } from '../hooks/useApi';
import { SeatCapability, SeatContext } from '../types';

/**
 * Whether this session is a team member sitting on someone else's account.
 *
 * Reads the entitlement response rather than a call of its own: the shell has
 * to decide what to render before its first paint, and a second request in
 * flight would flash the owner's nav — including Billing — at a team member for the
 * length of a round trip.
 *
 * `isLoading` matters here in a way it usually does not. Treating "not loaded
 * yet" as "not a team member" is the wrong default, so callers that gate a surface
 * should wait rather than assume. Everything below is cosmetic regardless: the
 * backend refuses a team member on every endpoint they lack the capability for, and
 * this file cannot grant anything.
 */

const EMPTY: SeatContext = {
  is_seat: false,
  owner_email: null,
  capabilities: [],
  label: '',
};

export interface SeatSession {
  seat: SeatContext;
  isSeat: boolean;
  /** Whose inventory this session is working in, when it is a team member's. */
  ownerEmail: string | null;
  isLoading: boolean;
  can: (capability: SeatCapability) => boolean;
}

export function useSeatSession(): SeatSession {
  const { data: entitlement, isLoading } = useEntitlement();

  // `seat` is absent on a response from a backend that predates the feature.
  // Falling back to EMPTY makes that read as "an ordinary account", which is
  // the correct interpretation of a deployment with no seats in it.
  const seat = entitlement?.seat ?? EMPTY;

  return {
    seat,
    isSeat: seat.is_seat,
    ownerEmail: seat.owner_email,
    isLoading,
    // An owner is not a team member and is not limited by capabilities, so they can
    // do everything. Only a team member is checked against their grant.
    can: (capability: SeatCapability) =>
      !seat.is_seat || seat.capabilities.includes(capability),
  };
}
